import React, { useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import {
  toggleFacilityType,
  setAllFacilityTypes,
  setMinConfidence,
  selectRegion,
  toggleFacilityVisible,
} from '../../features/facility/facilitySlice';
import { setFacilityPanelOpen } from '../../features/ui/uiSlice';
import { facilityTypeColor, facilityTypeLabel } from '../../utils/facility';
import { VisibilityIcon } from '../icons/ContextMenuIcons';

/** Max region rows rendered in the list (keeps the DOM light for 1000+ zones). */
const MAX_ROWS = 400;

const FacilityPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const name = useAppSelector((s) => s.facility.name);
  const regions = useAppSelector((s) => s.facility.regions);
  const visible = useAppSelector((s) => s.facility.visible);
  const visibleTypes = useAppSelector((s) => s.facility.visibleTypes);
  const minConfidence = useAppSelector((s) => s.facility.minConfidence);
  const selectedId = useAppSelector((s) => s.facility.selectedId);

  const [query, setQuery] = useState('');

  // Region counts per type (for the legend rows).
  const typeCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of regions) m[r.type] = (m[r.type] ?? 0) + 1;
    return m;
  }, [regions]);

  const types = useMemo(() => Object.keys(visibleTypes), [visibleTypes]);

  // Regions matching the current filters (type visibility, confidence, search).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return regions.filter((r) => {
      if (visibleTypes[r.type] === false) return false;
      if (r.confidence < minConfidence) return false;
      if (q) {
        const hay = `${r.name} ${r.layer} ${r.type}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [regions, visibleTypes, minConfidence, query]);

  if (!name || regions.length === 0) {
    return (
      <div className="h-full flex flex-col text-sm">
        <PanelHeader onClose={() => dispatch(setFacilityPanelOpen(false))} />
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <p className="text-xs text-gray-400">
            No facility analysis imported. Use{' '}
            <span className="font-mono">🗺️ Import facility analysis</span> after loading the DXF
            floor plan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col text-sm">
      <PanelHeader onClose={() => dispatch(setFacilityPanelOpen(false))} />

      {/* File + summary */}
      <div className="px-3 py-2 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-gray-400">📊</span>
          <span className="flex-1 truncate text-xs text-gray-600" title={name}>
            {name}
          </span>
          <button
            onClick={() => dispatch(toggleFacilityVisible())}
            className="text-[11px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
            title="Toggle overlay"
          >
            <VisibilityIcon visible={visible} className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="mt-1 text-[11px] text-gray-400">
          {regions.length} regions • {filtered.length} shown
        </div>
      </div>

      {/* Type legend / toggles */}
      <div className="px-3 py-2 border-b border-gray-200 shrink-0 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            Zone types
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => dispatch(setAllFacilityTypes(true))}
              className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              All
            </button>
            <button
              onClick={() => dispatch(setAllFacilityTypes(false))}
              className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              None
            </button>
          </div>
        </div>
        {types.map((t) => (
          <div
            key={t}
            onClick={() => dispatch(toggleFacilityType(t))}
            className={`flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer text-xs hover:bg-gray-100 ${
              visibleTypes[t] ? '' : 'opacity-50'
            }`}
          >
            <span
              className="w-3 h-3 rounded-sm shrink-0 border border-black/10"
              style={{ background: facilityTypeColor(t) }}
            />
            <span className="flex-1 truncate text-gray-800">{facilityTypeLabel(t)}</span>
            <span className="text-[10px] text-gray-400 tabular-nums">{typeCounts[t] ?? 0}</span>
            <VisibilityIcon visible={!!visibleTypes[t]} className="w-3.5 h-3.5 text-gray-600" />
          </div>
        ))}
      </div>

      {/* Confidence filter */}
      <div className="px-3 py-2 border-b border-gray-200 shrink-0">
        <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
          <span>Min confidence</span>
          <span className="tabular-nums">{Math.round(minConfidence * 100)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={0.9}
          step={0.1}
          value={minConfidence}
          onChange={(e) => dispatch(setMinConfidence(parseFloat(e.target.value)))}
          className="w-full"
        />
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-200 shrink-0">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name / layer…"
          className="input-field w-full text-xs"
        />
      </div>

      {/* Region list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-2 space-y-0.5">
        {filtered.length === 0 && (
          <div className="text-xs text-gray-400 italic px-2 py-2">No regions match.</div>
        )}
        {filtered.slice(0, MAX_ROWS).map((r) => {
          const isSel = r.id === selectedId;
          return (
            <div
              key={r.id}
              onClick={() => dispatch(selectRegion(isSel ? null : r.id))}
              className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs border transition-colors ${
                isSel
                  ? 'border-sky-400 bg-sky-50'
                  : 'border-transparent hover:bg-gray-100'
              }`}
              title={`${r.layer}\nconfidence ${Math.round(r.confidence * 100)}% • ${r.equipmentCount} equipment`}
            >
              <span
                className="w-2.5 h-2.5 rounded-sm shrink-0 border border-black/10"
                style={{ background: facilityTypeColor(r.type) }}
              />
              <span className="flex-1 truncate text-gray-800">
                {r.name || facilityTypeLabel(r.type)}
                {r.layer && (
                  <span className="text-gray-400 font-mono"> · {r.layer.replace(/^.*\$/, '')}</span>
                )}
              </span>
              <span className="text-[10px] text-gray-400 tabular-nums shrink-0">
                {r.equipmentCount}⚙
              </span>
            </div>
          );
        })}
        {filtered.length > MAX_ROWS && (
          <div className="text-[10px] text-gray-400 italic px-2 py-2 text-center">
            Showing first {MAX_ROWS} of {filtered.length}. Refine with search or filters.
          </div>
        )}
      </div>
    </div>
  );
};

const PanelHeader: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 shrink-0">
    <span className="font-semibold text-gray-900">Facility Zones</span>
    <div className="flex-1" />
    <button
      onClick={onClose}
      title="Close panel"
      className="w-5 h-5 flex items-center justify-center text-xs text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200"
    >
      ✕
    </button>
  </div>
);

export default FacilityPanel;
